/**
 * Timeshift service for demo purposes
 * Allows shifting all timestamps by a fixed offset to show historical market data
 */

const TIMESHIFT_STORAGE_KEY = 'yv_timeshift_offset_hours_v1';

/**
 * Get the timeshift offset in milliseconds
 * Reads from localStorage (synced with settings)
 * Negative values shift backward in time (e.g., -24 hours = 1 day ago)
 * Set to 0 to disable timeshifting
 */
function getTimeshiftOffsetInternal(): number {
  try {
    const stored = localStorage.getItem(TIMESHIFT_STORAGE_KEY);
    if (stored !== null) {
      const hours = JSON.parse(stored);
      return hours * 60 * 60 * 1000; // Convert hours to milliseconds
    }
  } catch {
    // Ignore localStorage errors
  }
  
  // Default: shift back 24 hours (1 day) for demo
  return -24 * 60 * 60 * 1000; // -86400000 ms = -1 day
}

/**
 * Get the current time adjusted by the timeshift offset
 * Use this instead of Date.now() throughout the app
 */
export function getCurrentTime(): number {
  return Date.now() + getTimeshiftOffsetInternal();
}

/**
 * Set the timeshift offset in hours
 * @param offsetHours - Offset in hours (negative = past, positive = future)
 */
export function setTimeshiftOffsetHours(offsetHours: number): void {
  try {
    localStorage.setItem(TIMESHIFT_STORAGE_KEY, JSON.stringify(offsetHours));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get the timeshift offset in hours (for display/debugging)
 */
export function getTimeshiftOffsetHours(): number {
  try {
    const stored = localStorage.getItem(TIMESHIFT_STORAGE_KEY);
    if (stored !== null) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore localStorage errors
  }
  return -24; // Default: -24 hours
}

/**
 * Disable timeshifting (set offset to 0)
 */
export function disableTimeshift(): void {
  setTimeshiftOffsetHours(0);
}

/**
 * Get the timeshift offset in milliseconds
 */
export function getTimeshiftOffsetMs(): number {
  return getTimeshiftOffsetInternal();
}

